import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { PatientFormData } from '@/types/forms';

let authToken: string | null = null;

export const setAuthToken = (token?: string | null) => {
  authToken = token ?? null;
};

export const getAuthToken = () => authToken;

export const clearAuthToken = () => {
  authToken = null;
};

export interface QueryEnvelope<T> {
  value: T;
  fallback: boolean;
  message?: string;
}

const wrapValue = <T>(value: T, fallback = false, message?: string): QueryEnvelope<T> => ({
  value,
  fallback,
  message,
});

const toHookResult = <T>(
  query: UseQueryResult<QueryEnvelope<T>>,
  fallbackValue: T,
) => ({
  ...query,
  data: query.data?.value ?? fallbackValue,
  isFallback: query.data?.fallback ?? false,
  fallbackMessage: query.data?.message,
});

export class ApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(
  input: RequestInfo,
  schema: z.ZodSchema<T>,
  init?: RequestInit,
): Promise<T> => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  if (init?.headers) {
    const incoming = new Headers(init.headers);
    incoming.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  const response = await fetch(input, {
    ...init,
    headers,
  });

  if (!response.ok) {
    throw new ApiError(`Request failed (${response.status})`, response.status);
  }

  const payload = await response.json();
  return schema.parse(payload);
};

// Schemas & types
export const patientSchema = z.object({
  id: z.string().or(z.number()),
  name: z.string(),
  age: z.number().nonnegative(),
  species: z.string(),
  owner: z.string().optional(),
  lastVisit: z.string().optional(),
  doctor: z.string().optional(),
});
export type Patient = z.infer<typeof patientSchema>;
const patientsSchema = z.array(patientSchema);

export const appointmentSchema = z.object({
  id: z.string().or(z.number()),
  date: z.string(),
  petName: z.string(),
  vetName: z.string(),
  type: z.string().optional(),
  room: z.string().optional(),
  owner: z.string().optional(),
  status: z.string().optional(),
});
export type Appointment = z.infer<typeof appointmentSchema>;
const appointmentsSchema = z.array(appointmentSchema);

const ROLE_LITERALS = ['medical_director', 'surgery', 'internal_med', 'customer_service', 'billing'] as const;
export type RoleSlug = (typeof ROLE_LITERALS)[number];

const isRoleSlug = (value: string): value is RoleSlug =>
  (ROLE_LITERALS as readonly string[]).includes(value);

export const roleSchema = z
  .string()
  .refine(isRoleSlug, { message: 'Invalid role assignment' }) as z.ZodType<RoleSlug>;

const resourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  location: z.string().nullable().optional(),
  status: z.string().optional(),
  metadata: z.any().optional(),
});
export type Resource = z.infer<typeof resourceSchema>;
const resourcesSchema = z.object({
  value: z.array(resourceSchema),
  fallback: z.boolean(),
  message: z.string().optional(),
});

const resourceBookingSchema = z.object({
  id: z.string(),
  resourceId: z.string(),
  patientId: z.string().or(z.number()).nullable().optional(),
  appointmentId: z.string().or(z.number()).nullable().optional(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.string().optional(),
  surgeon: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  waitlist: z.boolean().optional(),
});
export type ResourceBooking = z.infer<typeof resourceBookingSchema>;
const resourceBookingsSchema = z.object({
  value: z.array(resourceBookingSchema),
  fallback: z.boolean(),
  message: z.string().optional(),
});

const bedSchema = z.object({
  id: z.string(),
  label: z.string(),
  ward: z.string().optional(),
  status: z.string().optional(),
  turnsRequired: z.number().optional(),
  notes: z.string().nullable().optional(),
});
export type Bed = z.infer<typeof bedSchema>;
const bedStaySchema = z.object({
  id: z.string(),
  bedId: z.string(),
  patientId: z.string().or(z.number()),
  startedAt: z.string(),
  plannedDischargeAt: z.string().nullable().optional(),
  status: z.string().optional(),
  acuity: z.string().optional(),
  attending: z.string().nullable().optional(),
  handoffNote: z.string().nullable().optional(),
});
export type BedStay = z.infer<typeof bedStaySchema>;

const bedWhiteboardSchema = z.object({
  value: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      ward: z.string().optional(),
      status: z.string().optional(),
      turnsRequired: z.number().optional(),
      notes: z.string().nullable().optional(),
      stay: bedStaySchema.nullable(),
      tasks: z
        .array(
          z.object({
            id: z.string(),
            patientId: z.string().or(z.number()),
            title: z.string(),
            dueAt: z.string(),
            type: z.string(),
            status: z.string(),
            instructions: z.string().nullable().optional(),
          }),
        )
        .optional(),
      handoffs: z
        .array(
          z.object({
            id: z.string(),
            patientId: z.string().or(z.number()),
            shift: z.string(),
            summary: z.string(),
            acuity: z.string().optional(),
            author: z.string(),
            createdAt: z.string(),
          }),
        )
        .optional(),
    }),
  ),
  fallback: z.boolean(),
});
export type BedWhiteboard = z.infer<typeof bedWhiteboardSchema>['value'];

const icuWhiteboardSchema = z.object({
  value: z.array(
    z.object({
      patientId: z.string().or(z.number()),
      bedId: z.string(),
      acuity: z.string().optional(),
      attending: z.string().nullable().optional(),
      handoffNote: z.string().nullable().optional(),
      tasks: z.array(
        z.object({
          id: z.string(),
          patientId: z.string().or(z.number()),
          title: z.string(),
          dueAt: z.string(),
          type: z.string(),
          status: z.string(),
          instructions: z.string().nullable().optional(),
        }),
      ),
      handoffs: z.array(
        z.object({
          id: z.string(),
          patientId: z.string().or(z.number()),
          shift: z.string(),
          summary: z.string(),
          acuity: z.string().optional(),
          author: z.string(),
          createdAt: z.string(),
        }),
      ),
    }),
  ),
  fallback: z.boolean(),
});
export type IcuWhiteboard = z.infer<typeof icuWhiteboardSchema>['value'];

const handoffSchema = z.object({
  id: z.string(),
  patientId: z.string().or(z.number()),
  shift: z.string(),
  summary: z.string(),
  acuity: z.string().optional(),
  author: z.string(),
  createdAt: z.string(),
});
export type HandoffNote = z.infer<typeof handoffSchema>;
const handoffListSchema = z.object({
  value: z.array(handoffSchema),
  fallback: z.boolean(),
});

const careTaskSchema = z.object({
  id: z.string(),
  patientId: z.string().or(z.number()),
  title: z.string(),
  dueAt: z.string(),
  type: z.string(),
  status: z.string(),
  instructions: z.string().nullable().optional(),
});
export type CareTask = z.infer<typeof careTaskSchema>;

// Care Orders Schema
// Enumerations (Zod v4 compatibility using refine on string)
export const CARE_ORDER_CATEGORIES = ['medication','lab','nursing','followup'] as const;
export type CareOrderCategory = typeof CARE_ORDER_CATEGORIES[number];
export const CARE_ORDER_PRIORITIES = ['Critical','High','Routine'] as const;
export type CareOrderPriority = typeof CARE_ORDER_PRIORITIES[number];
export const CARE_ORDER_STATUSES = ['open','acknowledged','in_progress','done','blocked'] as const;
export type CareOrderStatus = typeof CARE_ORDER_STATUSES[number];

const literalStringEnum = <T extends readonly string[]>(values: T) =>
  z.string().refine((v) => (values as readonly string[]).includes(v), {
    message: `Invalid value. Expected one of: ${(values as readonly string[]).join(', ')}`,
  }) as unknown as z.ZodType<T[number]>;

const careOrderSignoffSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  step: z.number(),
  role: z.string(),
  label: z.string(),
  description: z.string().optional(),
  status: z.string(),
  assignee: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  signedAt: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  slaMinutes: z.number().nullable().optional(),
  slaBreached: z.boolean().optional(),
});
export type CareOrderSignoff = z.infer<typeof careOrderSignoffSchema>;

const careOrderHandoffSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  fromRole: z.string(),
  toRole: z.string(),
  handoffNote: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string(),
  acceptedBy: z.string().nullable().optional(),
  acceptedAt: z.string().nullable().optional(),
  status: z.string(),
});
export type CareOrderHandoff = z.infer<typeof careOrderHandoffSchema>;

const careOrderSchema = z.object({
  id: z.string(),
  patientId: z.string().or(z.number()),
  category: literalStringEnum(CARE_ORDER_CATEGORIES),
  title: z.string(),
  details: z.string(),
  priority: literalStringEnum(CARE_ORDER_PRIORITIES),
  status: literalStringEnum(CARE_ORDER_STATUSES),
  assignedRole: z.string(), // RN, ICU tech, Lab, Front desk
  assignedTo: z.string().nullable().optional(),
  dueAt: z.string().nullable().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string(),
  version: z.number(),
  blockReason: z.string().nullable().optional(),
  signoffs: z.array(careOrderSignoffSchema).optional(),
  latestHandoff: careOrderHandoffSchema.nullable().optional(),
  pendingHandoff: careOrderHandoffSchema.nullable().optional(),
  slaBreached: z.boolean().optional(),
});
export type CareOrder = z.infer<typeof careOrderSchema>;

const careOrderListSchema = z.object({
  value: z.array(careOrderSchema),
  fallback: z.boolean(),
  message: z.string().optional(),
});

const careOrderHistorySchema = z.object({
  id: z.string(),
  orderId: z.string(),
  version: z.number(),
  changes: z.record(z.string(), z.any()),
  changedBy: z.string(),
  changedAt: z.string(),
  action: z.string(), // create, update, complete, block, restore
});
export type CareOrderHistory = z.infer<typeof careOrderHistorySchema>;

const careOrderHistoryListSchema = z.object({
  value: z.array(careOrderHistorySchema),
  fallback: z.boolean(),
});

const medSafetySchema = z.object({
  medication: z.string(),
  doseMgKg: z.number().nullable().optional(),
  patientWeightKg: z.number().optional(),
  weightBasedDoseMg: z.number().nullable().optional(),
  recommendedRange: z.tuple([z.number(), z.number()]).nullable().optional(),
  warnings: z.array(z.string()),
});
export type MedSafetyCheck = z.infer<typeof medSafetySchema>;

const soapTemplateSchema = z.object({
  id: z.string(),
  diagnosis: z.string(),
  soap: z.object({
    subjective: z.string(),
    objective: z.string(),
    assessment: z.string(),
    plan: z.string(),
  }),
  meds: z.array(
    z.object({
      name: z.string(),
      dosageRangeMgKg: z.tuple([z.number(), z.number()]).optional(),
      frequency: z.string().optional(),
    }),
  ),
});
export type SoapTemplate = z.infer<typeof soapTemplateSchema>;
const soapTemplateListSchema = z.object({
  value: z.array(soapTemplateSchema),
  fallback: z.boolean(),
});
const soapGeneratedSchema = z.object({
  diagnosis: z.string(),
  soap: soapTemplateSchema.shape.soap,
  meds: soapTemplateSchema.shape.meds,
  notes: z.string(),
});

const quoteSchema = z.object({
  id: z.string(),
  client: z.string(),
  patient: z.string(),
  estimate: z.string(),
  validUntil: z.string(),
  status: z.string(),
  signedBy: z.string().nullable().optional(),
  invoiceId: z.string().nullable().optional(),
});
export type Quote = z.infer<typeof quoteSchema>;

const notifySchema = z.object({
  id: z.string(),
  channel: z.string(),
  to: z.string(),
  message: z.string(),
  scheduledAt: z.string().nullable().optional(),
  status: z.string(),
  createdAt: z.string().nullable().optional(),
  metadata: z.any().optional(),
});
export type NotificationStub = z.infer<typeof notifySchema>;

const paginationMetaSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  totalPages: z.number(),
  hasMore: z.boolean(),
});
const severityStatsSchema = z.object({
  severityCounts: z.record(z.string(), z.number()),
});

const notificationsListSchema = z.object({
  value: z.array(notifySchema),
  meta: paginationMetaSchema,
  fallback: z.boolean().optional(),
});
export type NotificationsResponse = z.infer<typeof notificationsListSchema>;

const clientErrorLogSchema = z.object({
  id: z.string(),
  message: z.string(),
  stack: z.string().nullable().optional(),
  componentStack: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  severity: z.string().nullable().optional(),
  metadata: z.string().nullable().optional(),
  createdAt: z.string(),
  sentrySearchUrl: z.string().nullable().optional(),
});
export type ClientErrorLog = z.infer<typeof clientErrorLogSchema>;

const clientErrorLogsSchema = z.object({
  value: z.array(clientErrorLogSchema),
  meta: paginationMetaSchema,
  stats: severityStatsSchema.optional(),
  fallback: z.boolean().optional(),
});

const inventoryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  sku: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  minThreshold: z.number().optional(),
  quantityOnHand: z.number().optional(),
  batches: z
    .array(
      z.object({
        id: z.string(),
        itemId: z.string(),
        lot: z.string().nullable().optional(),
        expiry: z.string().nullable().optional(),
        quantityOnHand: z.number().optional(),
        costCents: z.number().optional(),
      }),
    )
    .optional(),
});
export type InventoryItem = z.infer<typeof inventoryItemSchema>;
const inventoryItemsSchema = z.object({
  value: z.array(inventoryItemSchema),
  fallback: z.boolean(),
  message: z.string().optional(),
});

const inventoryAlertSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  status: z.string(),
  message: z.string(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable().optional(),
});
export type InventoryAlert = z.infer<typeof inventoryAlertSchema>;
const inventoryAlertsSchema = z.object({
  value: z.array(inventoryAlertSchema),
  fallback: z.boolean(),
  message: z.string().optional(),
});

const carePlanRound = z.object({
  patient: z.string(),
  diagnosis: z.string(),
  priority: z.string(),
  attending: z.string(),
  nextStep: z.string(),
});
const careReminder = z.object({
  title: z.string(),
  due: z.string(),
  channel: z.string(),
});
const surgeryEntry = z.object({
  time: z.string(),
  case: z.string(),
  room: z.string(),
  lead: z.string(),
});

export interface CarePlanPayload {
  rounds: z.infer<typeof carePlanRound>[];
  reminders: z.infer<typeof careReminder>[];
  surgeryBoard: z.infer<typeof surgeryEntry>[];
  inventoryAlerts: InventoryAlert[];
  updatedAt?: string;
}

const carePlanSchema = z.object({
  rounds: z.array(carePlanRound),
  reminders: z.array(careReminder),
  surgeryBoard: z.array(surgeryEntry),
  inventoryAlerts: z.array(inventoryAlertSchema),
  updatedAt: z.string().optional(),
});

// Care Plan Versioning Schemas
const carePlanVersionSchema = z.object({
  id: z.string(),
  carePlanId: z.number(),
  versionNumber: z.number(),
  status: z.string(), // draft | submitted | approved | rejected | archived
  type: z.string(),   // draft | formal
  summary: z.string().optional(),
  changeNote: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  publishedAt: z.string().optional(),
  restoredFromVersion: z.number().optional(),
  content: z.any(),
});
export type CarePlanVersion = z.infer<typeof carePlanVersionSchema>;
// Paginated wrapper schema
const paginatedCarePlanVersionsSchema = z.object({
  items: z.array(carePlanVersionSchema),
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

const carePlanReviewSchema = z.object({
  id: z.string(),
  action: z.string(), // submit | approve | reject | comment | restore | signoff events
  actor: z.string(),
  actorRole: z.string().optional(),
  comment: z.string().optional(),
  createdAt: z.string(),
});
export type CarePlanReview = z.infer<typeof carePlanReviewSchema>;
const carePlanSignoffSchema = z.object({
  id: z.string(),
  role: z.string(),
  signer: z.string().optional(),
  signedAt: z.string().optional(),
  status: z.string(), // pending | signed | revoked
});
export type CarePlanSignoff = z.infer<typeof carePlanSignoffSchema>;
const carePlanHistorySchema = z.object({
  reviews: z.array(carePlanReviewSchema),
  signoffs: z.array(carePlanSignoffSchema),
});
export type CarePlanHistory = z.infer<typeof carePlanHistorySchema>;
const carePlanDiffSchema = z.object({
  base: carePlanVersionSchema,
  other: carePlanVersionSchema,
  diff: z.array(
    z.object({
      key: z.string(),
      a: z.any(),
      b: z.any(),
      changed: z.boolean(),
    }),
  ),
});
export type CarePlanDiff = z.infer<typeof carePlanDiffSchema>;

const invoiceSchema = z.object({
  id: z.string(),
  client: z.string(),
  patient: z.string(),
  balance: z.string(),
  status: z.string(),
  due: z.string(),
});
const insuranceSchema = z.object({
  carrier: z.string(),
  claim: z.string(),
  amount: z.string(),
  stage: z.string(),
});
const spendSchema = z.object({
  category: z.string(),
  amount: z.string(),
  trend: z.string(),
});

export interface BillingWorkspacePayload {
  invoices: z.infer<typeof invoiceSchema>[];
  insuranceQueue: z.infer<typeof insuranceSchema>[];
  inventorySpend: z.infer<typeof spendSchema>[];
}

const billingSchema: z.ZodSchema<BillingWorkspacePayload> = z.object({
  invoices: z.array(invoiceSchema),
  insuranceQueue: z.array(insuranceSchema),
  inventorySpend: z.array(spendSchema),
});

export const patientNoteSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  author: z.string(),
  role: z.string(),
  category: z.string(),
  note: z.string(),
  createdAt: z.string(),
});
export type PatientNote = z.infer<typeof patientNoteSchema>;
const patientNotesSchema = z.array(patientNoteSchema);

const prescriptionSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  medication: z.string(),
  dosage: z.string().optional(),
  frequency: z.string().optional(),
  duration: z.string().optional(),
  instructions: z.string().optional(),
  status: z.string().optional(),
  createdAt: z.string(),
});
export type Prescription = z.infer<typeof prescriptionSchema>;
const prescriptionsSchema = z.array(prescriptionSchema);

const labResultSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  testName: z.string(),
  status: z.string().optional(),
  findings: z.string().optional(),
  performedAt: z.string().nullable().optional(),
  attachmentId: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type LabResult = z.infer<typeof labResultSchema>;
const labResultsSchema = z.array(labResultSchema);

const visitSchema = z.object({
  id: z.string(),
  patientId: z.string().or(z.number()),
  vetId: z.string().or(z.number()).nullable().optional(),
  visitDate: z.string().nullable().optional(),
  reason: z.string().optional(),
  disposition: z.string().optional(),
  createdAt: z.string(),
});
export type PatientVisit = z.infer<typeof visitSchema>;
const visitsSchema = z.array(visitSchema);

const medicalRecordSchema = z.object({
  id: z.string(),
  // Accept number or string from API, normalize to string safely.
  patientId: z.preprocess((val) => (typeof val === 'number' ? val.toString() : val), z.string()),
  visitId: z.string().nullable().optional(),
  visitDate: z.string().nullable().optional(),
  diagnosis: z.string().optional(),
  assessment: z.string().optional(),
  treatmentPlan: z.string().optional(),
  vitals: z.string().optional(),
  labSummary: z.string().optional(),
  imagingSummary: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});
export type MedicalRecord = z.infer<typeof medicalRecordSchema>;
const medicalRecordsSchema = z.array(medicalRecordSchema);

const attachmentSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  recordId: z.string().optional(),
  filename: z.string(),
  contentType: z.string().optional(),
  contentBase64: z.string().optional(),
  sizeBytes: z.number().optional(),
  source: z.string().optional(),
  createdAt: z.string(),
});
export type PatientAttachment = z.infer<typeof attachmentSchema>;
const attachmentsSchema = z.array(attachmentSchema);

const appointmentActionSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  previousStatus: z.string().optional(),
  updatedAt: z.string().optional(),
});
const reminderResponseSchema = z.object({
  id: z.string(),
  channel: z.string(),
  sentAt: z.string(),
});
const billingActionSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  balance: z.string().optional(),
  updatedAt: z.string().optional(),
});

const opsMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
  trend: z.string(),
});

const opsStatusSchema = z.object({
  status: z.string(),
  count: z.number(),
});

const opsSummarySchema = z.object({
  metrics: z.array(opsMetricSchema),
  appointmentStatus: z.array(opsStatusSchema),
  revenue: z.object({
    pending: z.number(),
    collected: z.number(),
  }),
});

export type OpsSummary = z.infer<typeof opsSummarySchema>;

export interface PrefetchBundle {
  patients: Patient[];
  appointments: Appointment[];
  carePlans: CarePlanPayload;
  billing: BillingWorkspacePayload;
  ops: OpsSummary;
}

const authUserSchema = z.object({
  id: z.number().optional(),
  email: z.string().email(),
  name: z.string(),
  role: roleSchema.nullable().optional(),
  permissions: z.array(z.string()).optional(),
  demo: z.boolean().optional(),
});
export type AuthenticatedUser = z.infer<typeof authUserSchema>;

const loginResponseSchema = z.object({
  token: z.string(),
  user: authUserSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const auditLogEntrySchema = z.object({
  id: z.string(),
  actor: z.string(),
  role: z.string(),
  action: z.string(),
  resource: z.string(),
  timestamp: z.string(),
  detail: z.string().optional(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;
const auditLogSchema = z.array(auditLogEntrySchema);

// Fallback data
const fallbackPatients: Patient[] = [
  {
    id: 'luna',
    name: 'Luna',
    age: 4,
    species: 'canine',
    owner: 'Mia Lin',
    doctor: 'Dr. Avery',
    lastVisit: 'Mar 3, 2025',
  },
  {
    id: 'max',
    name: 'Max',
    age: 7,
    species: 'canine',
    owner: 'Leo Chang',
    doctor: 'Dr. Patel',
    lastVisit: 'Feb 28, 2025',
  },
  {
    id: 'bailey',
    name: 'Bailey',
    age: 6,
    species: 'feline',
    owner: 'Owen Wu',
    doctor: 'Dr. Ramos',
    lastVisit: 'Mar 1, 2025',
  },
];

const fallbackAppointments: Appointment[] = [
  {
    id: 'appt-1',
    date: new Date().toISOString(),
    petName: 'Luna',
    owner: 'Mia Lin',
    vetName: 'Dr. Avery',
    type: 'Triage',
    room: 'Treatment A',
    status: 'Check-in',
  },
  {
    id: 'appt-2',
    date: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    petName: 'Max',
    owner: 'Leo Chang',
    vetName: 'Dr. Patel',
    type: 'Surgery prep',
    room: 'OR 2',
    status: 'Pre-op',
  },
  {
    id: 'appt-3',
    date: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    petName: 'Bailey',
    owner: 'Owen Wu',
    vetName: 'Dr. Ramos',
    type: 'Follow up',
    room: 'Exam 4',
    status: 'Follow-up',
  },
];

const fallbackCarePlans: CarePlanPayload = {
  rounds: [
    {
      patient: 'Luna',
      diagnosis: 'Immune-mediated hemolytic anemia',
      priority: 'Critical',
      attending: 'Dr. Patel',
      nextStep: 'Transfusion review',
    },
    {
      patient: 'Kobe',
      diagnosis: 'TPLO post-op day 1',
      priority: 'High',
      attending: 'Dr. Ramos',
      nextStep: 'Pain reassessment 18:00',
    },
    {
      patient: 'Miso',
      diagnosis: 'Pancreatitis',
      priority: 'Moderate',
      attending: 'Dr. Alvarez',
      nextStep: 'Re-evaluate hydration',
    },
  ],
  reminders: [
    { title: 'Bailey - Sutures removal', due: 'Tomorrow 09:00', channel: 'SMS + Email' },
    { title: 'Ori - Cardiology recheck', due: 'Fri 14:30', channel: 'Owner portal' },
    { title: 'Nala - Vaccine booster', due: 'Next week', channel: 'Push notification' },
  ],
  surgeryBoard: [
    { time: '08:00', case: 'Luna - splenectomy', room: 'OR 2', lead: 'Dr. Patel' },
    { time: '10:30', case: 'Atlas - orthopedics consult', room: 'Ortho', lead: 'Dr. Ramos' },
    { time: '13:15', case: 'Nova - ophthalmology', room: 'Procedure', lead: 'Dr. Khan' },
  ],
  inventoryAlerts: [
    { id: 'alert-iso', itemId: 'inv-iso', status: 'low', message: 'Isoflurane ~18 hrs on hand', createdAt: new Date().toISOString() },
    { id: 'alert-fentanyl', itemId: 'inv-fentanyl', status: 'low', message: 'Fentanyl patches 8 remaining', createdAt: new Date().toISOString() },
    { id: 'alert-suture', itemId: 'inv-suture', status: 'ordered', message: 'Sutures 3-0 reorder placed, ETA 2 days', createdAt: new Date().toISOString() },
  ],
  updatedAt: '2024-01-01T08:00:00.000Z',
};

const fallbackBillingWorkspace: BillingWorkspacePayload = {
  invoices: [
    { id: 'INV-9201', client: 'Mia Lin', patient: 'Luna', balance: '$1,240', status: 'Pending', due: 'Today' },
    { id: 'INV-9195', client: 'Leo Chang', patient: 'Max', balance: '$780', status: 'Installments', due: 'Next week' },
    { id: 'INV-9192', client: 'Ava Huang', patient: 'Nova', balance: '$320', status: 'Paid', due: 'Cleared' },
  ],
  insuranceQueue: [
    { carrier: 'Trupanion', claim: '#T-4211', amount: '$2,310', stage: 'Review' },
    { carrier: 'Nationwide', claim: '#N-1880', amount: '$890', stage: 'Submitted' },
  ],
  inventorySpend: [
    { category: 'Pharmacy', amount: '$4,520', trend: '+8% vs last week' },
    { category: 'Surgery supplies', amount: '$3,180', trend: '-2%' },
    { category: 'Lab services', amount: '$1,450', trend: '+4%' },
  ],
};

const fallbackPatientNotes: Record<string, PatientNote[]> = {
  luna: [
    {
      id: 'note-luna-1',
      patientId: 'luna',
      author: 'Dr. Avery',
      role: 'Medical Director',
      category: 'Rounds',
      note: 'HCT stabilized after transfusion. Continue immunosuppressants and taper fluids over 8 hrs.',
      createdAt: '2025-03-05T09:00:00Z',
    },
    {
      id: 'note-luna-2',
      patientId: 'luna',
      author: 'RN Kai',
      role: 'ICU Nurse',
      category: 'Nursing',
      note: 'Patient eating 50% of breakfast, resting comfortably. Next vitals at 11:00.',
      createdAt: '2025-03-05T08:15:00Z',
    },
  ],
  max: [
    {
      id: 'note-max-1',
      patientId: 'max',
      author: 'Dr. Ramos',
      role: 'Surgeon',
      category: 'Surgery',
      note: 'Reviewed TPLO post-op films with owner. Reinforce restricted activity for 6 weeks.',
      createdAt: '2025-03-04T16:45:00Z',
    },
  ],
  default: [
    {
      id: 'note-default-1',
      patientId: 'default',
      author: 'System',
      role: 'Automation',
      category: 'General',
      note: 'New patient onboarded. Awaiting first triage exam.',
      createdAt: new Date().toISOString(),
    },
  ],
};

const getFallbackPatientNotes = (patientId: string | number) => {
  const key = patientId.toString().toLowerCase();
  return (
    fallbackPatientNotes[patientId.toString()] ??
    fallbackPatientNotes[key] ??
    fallbackPatientNotes.default
  );
};

const fallbackPrescriptions: Record<string, Prescription[]> = {
  luna: [
    {
      id: 'rx-luna-1',
      patientId: 'luna',
      medication: 'Prednisone 5mg',
      dosage: '5mg',
      frequency: 'BID',
      duration: '14 days',
      instructions: 'Give with food, taper per plan',
      status: 'active',
      createdAt: '2025-03-05T08:00:00Z',
    },
  ],
  default: [],
};

const fallbackLabResults: Record<string, LabResult[]> = {
  luna: [
    {
      id: 'lab-luna-1',
      patientId: 'luna',
      testName: 'CBC',
      status: 'Completed',
      findings: 'PCV trending down, retics stable.',
      performedAt: '2025-03-05T06:45:00Z',
      attachmentId: null,
      createdAt: '2025-03-05T07:00:00Z',
    },
  ],
  default: [],
};

const fallbackAttachments: Record<string, PatientAttachment[]> = {
  luna: [
    {
      id: 'file-luna-1',
      patientId: 'luna',
      recordId: 'record-luna-1',
      filename: 'cbc_results.pdf',
      contentType: 'application/pdf',
      contentBase64: undefined,
      sizeBytes: 18200,
      source: 'upload',
      createdAt: '2025-03-05T07:05:00Z',
    },
  ],
  default: [],
};

const fallbackVisits: Record<string, PatientVisit[]> = {
  luna: [
    {
      id: 'visit-luna-1',
      patientId: 'luna',
      vetId: '4',
      visitDate: '2025-03-05T07:30:00Z',
      reason: 'Post-op monitoring',
      disposition: 'Continue ICU monitoring, analgesia',
      createdAt: '2025-03-05T07:30:00Z',
    },
  ],
  default: [],
};

const fallbackMedicalRecords: Record<string, MedicalRecord[]> = {
  luna: [
    {
      id: 'record-luna-1',
      patientId: 'luna',
      visitId: 'visit-luna-1',
      visitDate: '2025-03-05T07:30:00Z',
      diagnosis: 'Immune-mediated hemolytic anemia flare',
      assessment: 'Responding to transfusion, continue to monitor PCV.',
      treatmentPlan: 'Prednisone + azathioprine, GI protectants, repeat CBC in 48h.',
      vitals: 'T 101.5F, HR 120, RR 32',
      labSummary: 'PCV 25%, retics trending down',
      imagingSummary: 'Thoracic rads clear',
      notes: 'Owner updated via portal.',
      createdAt: '2025-03-05T07:45:00Z',
      updatedAt: '2025-03-05T07:45:00Z',
    },
  ],
  default: [],
};

const getFallbackCollection = <T>(collection: Record<string, T[]>, patientId?: string | number) => {
  if (!patientId) return collection.default;
  const key = patientId.toString().toLowerCase();
  return collection[patientId.toString()] ?? collection[key] ?? collection.default;
};

const fallbackAuditLog: AuditLogEntry[] = [
  {
    id: 'audit-1',
    actor: 'director@lkcmedical.com',
    role: 'medical_director',
    action: 'auth.login',
    resource: 'auth',
    detail: 'Session started via dashboard preview.',
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
  },
  {
    id: 'audit-2',
    actor: 'surgery@lkcmedical.com',
    role: 'surgery',
    action: 'patient.note.create',
    resource: 'patient:luna',
    detail: 'Rounds update added from OR tablet.',
    timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
  },
  {
    id: 'audit-3',
    actor: 'billing@lkcmedical.com',
    role: 'billing',
    action: 'billing.invoice.review',
    resource: 'invoice:INV-9201',
    detail: 'Payment plan approved.',
    timestamp: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
  },
];

const fallbackResources: Resource[] = [
  { id: 'res-or-1', name: 'OR 1', type: 'or', location: 'Surgery', status: 'available' },
  { id: 'res-or-2', name: 'OR 2', type: 'or', location: 'Surgery', status: 'available' },
  { id: 'res-xray', name: 'X-Ray Suite', type: 'imaging', location: 'Imaging', status: 'available' },
  { id: 'res-us', name: 'Ultrasound', type: 'ultrasound', location: 'Imaging', status: 'available' },
  { id: 'res-anesthesia-1', name: 'Anesthesia Machine 1', type: 'anesthesia', location: 'Surgery', status: 'available' },
];

const fallbackResourceBookings: ResourceBooking[] = [
  {
    id: 'rbk-1',
    resourceId: 'res-or-2',
    patientId: 'luna',
    appointmentId: 'appt-2',
    startTime: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    status: 'scheduled',
    surgeon: 'Dr. Ramos',
    note: 'TPLO + ICU bed hold',
    waitlist: false,
  },
  {
    id: 'rbk-2',
    resourceId: 'res-xray',
    patientId: 'bailey',
    appointmentId: 'appt-3',
    startTime: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() + 3.5 * 60 * 60 * 1000).toISOString(),
    status: 'scheduled',
    surgeon: 'Dr. Patel',
    note: 'Thoracic rads',
    waitlist: false,
  },
  {
    id: 'rbk-wait-1',
    resourceId: 'res-or-1',
    patientId: 'max',
    appointmentId: null,
    startTime: new Date(Date.now() + 90 * 60 * 1000).toISOString(),
    endTime: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
    status: 'waitlist',
    surgeon: 'Dr. Avery',
    note: 'Exploratory laparotomy if OR opens',
    waitlist: true,
  },
];

const fallbackBeds: Bed[] = [
  { id: 'bed-icu-1', label: 'ICU-1', ward: 'ICU', status: 'occupied', turnsRequired: 1, notes: 'Heated pad' },
  { id: 'bed-icu-2', label: 'ICU-2', ward: 'ICU', status: 'cleaning', turnsRequired: 0, notes: 'Deep clean' },
  { id: 'bed-ward-3', label: 'Ward-3', ward: 'Ward', status: 'open', turnsRequired: 0, notes: '' },
  { id: 'bed-ward-4', label: 'Ward-4', ward: 'Ward', status: 'reserved', turnsRequired: 0, notes: 'Hold for Max 14:00' },
];

const fallbackBedStays: BedStay[] = [
  {
    id: 'stay-luna',
    bedId: 'bed-icu-1',
    patientId: 'luna',
    startedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    plannedDischargeAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    status: 'occupied',
    acuity: 'critical',
    attending: 'Dr. Patel',
    handoffNote: 'q2 vitals; fentanyl CRI',
  },
];

const fallbackHandoffs: HandoffNote[] = [
  {
    id: 'handoff-1',
    patientId: 'luna',
    shift: 'Day ??Swing',
    summary: 'PCV stable, continue CRI, next CBC 18:00, owner update pending',
    acuity: 'critical',
    author: 'Dr. Patel',
    createdAt: new Date().toISOString(),
  },
  {
    id: 'handoff-2',
    patientId: 'max',
    shift: 'Day ??Swing',
    summary: 'NPO after midnight, OR slot pending, monitor pain q4h',
    acuity: 'high',
    author: 'Dr. Avery',
    createdAt: new Date().toISOString(),
  },
];

const fallbackCareTasks: CareTask[] = [
  {
    id: 'task-1',
    patientId: 'luna',
    title: 'Morphine 0.1 mg/kg IV',
    dueAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
    type: 'medication',
    status: 'pending',
    instructions: 'Confirm pain score before dose',
  },
  {
    id: 'task-2',
    patientId: 'luna',
    title: 'CBC',
    dueAt: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
    type: 'lab',
    status: 'pending',
    instructions: 'Send to in-house analyzer',
  },
  {
    id: 'task-3',
    patientId: 'bailey',
    title: 'Feed small meal',
    dueAt: new Date(Date.now() + 90 * 60 * 1000).toISOString(),
    type: 'care',
    status: 'pending',
    instructions: 'Check appetite',
  },
];

const fallbackCareOrders: CareOrder[] = [
  {
    id: 'order-1',
    patientId: 'bailey',
    category: 'medication',
    title: 'Prednisone taper',
    details: 'Start 2mg/kg BID, taper by 25% every week',
    priority: 'High',
    status: 'open',
    assignedRole: 'RN',
    assignedTo: null,
    dueAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    createdBy: 'Dr. Smith',
    updatedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    version: 1,
    signoffs: [
      {
        id: 'order-1-step-1',
        orderId: 'order-1',
        step: 1,
        role: 'attending',
        label: '主治醫師簽核',
        description: '確認醫囑內容',
        status: 'approved',
        assignee: 'Dr. Smith',
        note: null,
        signedAt: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 20).toISOString(),
        slaMinutes: 30,
        slaBreached: false,
      },
      {
        id: 'order-1-step-2',
        orderId: 'order-1',
        step: 2,
        role: 'nurse_manager',
        label: '護理主管覆核',
        description: '排程與交班檢查',
        status: 'pending',
        assignee: 'RN lead',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
      {
        id: 'order-1-step-3',
        orderId: 'order-1',
        step: 3,
        role: 'care_team',
        label: '責任團隊交班',
        description: 'ICU/病房/手術團隊接手',
        status: 'pending',
        assignee: 'ICU team',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
    ],
    latestHandoff: null,
    pendingHandoff: null,
    slaBreached: false,
  },
  {
    id: 'order-2',
    patientId: 'bailey',
    category: 'lab',
    title: 'CBC with differential',
    details: 'Monitor PCV, platelet count, WBC. Alert if PCV < 18%',
    priority: 'Critical',
    status: 'acknowledged',
    assignedRole: 'Lab',
    assignedTo: 'Tech-Maria',
    dueAt: new Date(Date.now() + 1 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    createdBy: 'Dr. Smith',
    updatedAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    version: 2,
    signoffs: [
      {
        id: 'order-2-step-1',
        orderId: 'order-2',
        step: 1,
        role: 'attending',
        label: '主治醫師簽核',
        description: '確認醫囑內容',
        status: 'approved',
        assignee: 'Dr. Smith',
        note: null,
        signedAt: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
        slaMinutes: 30,
        slaBreached: false,
      },
      {
        id: 'order-2-step-2',
        orderId: 'order-2',
        step: 2,
        role: 'nurse_manager',
        label: '護理主管覆核',
        description: '排程與交班檢查',
        status: 'approved',
        assignee: 'Charge RN',
        note: 'Lab window reserved',
        signedAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
      {
        id: 'order-2-step-3',
        orderId: 'order-2',
        step: 3,
        role: 'care_team',
        label: '責任團隊交班',
        description: 'ICU/病房/手術團隊接手',
        status: 'pending',
        assignee: 'Lab - Maria',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
    ],
    latestHandoff: {
      id: 'order-2-handoff',
      orderId: 'order-2',
      fromRole: 'nurse_manager',
      toRole: 'Lab',
      handoffNote: 'Urgent CBC prep',
      createdBy: 'charge-rn@lkcmedical.com',
      createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
      acceptedBy: null,
      acceptedAt: null,
      status: 'pending',
    },
    pendingHandoff: {
      id: 'order-2-handoff',
      orderId: 'order-2',
      fromRole: 'nurse_manager',
      toRole: 'Lab',
      handoffNote: 'Urgent CBC prep',
      createdBy: 'charge-rn@lkcmedical.com',
      createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
      acceptedBy: null,
      acceptedAt: null,
      status: 'pending',
    },
    slaBreached: false,
  },
  {
    id: 'order-3',
    patientId: 'max',
    category: 'nursing',
    title: 'Keep NPO 8 hours',
    details: 'No food or water before surgery. Water allowed up to 2hrs pre-op',
    priority: 'Critical',
    status: 'in_progress',
    assignedRole: 'ICU tech',
    assignedTo: 'Tech-John',
    dueAt: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    createdBy: 'Dr. Lee',
    updatedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    version: 1,
    signoffs: [
      {
        id: 'order-3-step-1',
        orderId: 'order-3',
        step: 1,
        role: 'attending',
        label: '主治醫師簽核',
        description: '確認醫囑內容',
        status: 'approved',
        assignee: 'Dr. Lee',
        note: null,
        signedAt: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
        slaMinutes: 30,
        slaBreached: false,
      },
      {
        id: 'order-3-step-2',
        orderId: 'order-3',
        step: 2,
        role: 'nurse_manager',
        label: '護理主管覆核',
        description: '排程與交班檢查',
        status: 'approved',
        assignee: 'ICU lead',
        note: 'NPO charted',
        signedAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 105).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
      {
        id: 'order-3-step-3',
        orderId: 'order-3',
        step: 3,
        role: 'care_team',
        label: '責任團隊交班',
        description: 'ICU/病房/手術團隊接手',
        status: 'in_progress',
        assignee: 'Tech-John',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 80).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
    ],
    latestHandoff: {
      id: 'order-3-handoff',
      orderId: 'order-3',
      fromRole: 'nurse_manager',
      toRole: 'ICU tech',
      handoffNote: 'NPO enforcement, alerts for owner updates',
      createdBy: 'charge-rn@lkcmedical.com',
      createdAt: new Date(Date.now() - 1000 * 60 * 80).toISOString(),
      acceptedBy: 'Tech-John',
      acceptedAt: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
      status: 'accepted',
    },
    pendingHandoff: null,
    slaBreached: false,
  },
  {
    id: 'order-4',
    patientId: 'luna',
    category: 'followup',
    title: 'Recheck radiographs',
    details: '2-view thorax to assess pneumonia resolution',
    priority: 'Routine',
    status: 'open',
    assignedRole: 'Front desk',
    assignedTo: null,
    dueAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    createdBy: 'Dr. Chen',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    version: 1,
    signoffs: [
      {
        id: 'order-4-step-1',
        orderId: 'order-4',
        step: 1,
        role: 'attending',
        label: '主治醫師簽核',
        description: '確認醫囑內容',
        status: 'pending',
        assignee: 'Dr. Chen',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        slaMinutes: 30,
        slaBreached: false,
      },
      {
        id: 'order-4-step-2',
        orderId: 'order-4',
        step: 2,
        role: 'nurse_manager',
        label: '護理主管覆核',
        description: '排程與交班檢查',
        status: 'pending',
        assignee: 'Front desk',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
      {
        id: 'order-4-step-3',
        orderId: 'order-4',
        step: 3,
        role: 'care_team',
        label: '責任團隊交班',
        description: 'ICU/病房/手術團隊接手',
        status: 'pending',
        assignee: 'Front desk',
        note: null,
        signedAt: null,
        createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
    ],
    latestHandoff: null,
    pendingHandoff: null,
    slaBreached: false,
  },
  {
    id: 'order-5',
    patientId: 'bailey',
    category: 'medication',
    title: 'Famotidine administration',
    details: '0.5mg/kg IV q12h for gastric protection',
    priority: 'Routine',
    status: 'done',
    assignedRole: 'RN',
    assignedTo: 'Nurse-Sarah',
    dueAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    createdBy: 'Dr. Smith',
    updatedAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    version: 1,
    signoffs: [
      {
        id: 'order-5-step-1',
        orderId: 'order-5',
        step: 1,
        role: 'attending',
        label: '主治醫師簽核',
        description: '確認醫囑內容',
        status: 'approved',
        assignee: 'Dr. Smith',
        note: null,
        signedAt: new Date(Date.now() - 1000 * 60 * 175).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 175).toISOString(),
        slaMinutes: 30,
        slaBreached: false,
      },
      {
        id: 'order-5-step-2',
        orderId: 'order-5',
        step: 2,
        role: 'nurse_manager',
        label: '護理主管覆核',
        description: '排程與交班檢查',
        status: 'approved',
        assignee: 'RN lead',
        note: null,
        signedAt: new Date(Date.now() - 1000 * 60 * 150).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 170).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 150).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
      {
        id: 'order-5-step-3',
        orderId: 'order-5',
        step: 3,
        role: 'care_team',
        label: '責任團隊交班',
        description: 'ICU/病房/手術團隊接手',
        status: 'approved',
        assignee: 'Nurse-Sarah',
        note: 'Dose documented',
        signedAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
        createdAt: new Date(Date.now() - 1000 * 60 * 160).toISOString(),
        updatedAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
        slaMinutes: null,
        slaBreached: false,
      },
    ],
    latestHandoff: null,
    pendingHandoff: null,
    slaBreached: false,
  },
];

const fallbackSoapTemplates: SoapTemplate[] = [
  {
    id: 'tpl-imha',
    diagnosis: 'IMHA',
    soap: {
      subjective: 'Lethargy, pale mucous membranes',
      objective: 'PCV trending up after transfusion, HR 120, RR 32',
      assessment: 'Immune mediated hemolytic anemia improving',
      plan: 'Pred + azathioprine, GI protectants, CBC q6h, transfusion threshold PCV < 18%',
    },
    meds: [{ name: 'Prednisone', dosageRangeMgKg: [1, 2], frequency: 'BID' }],
  },
  {
    id: 'tpl-pancreatitis',
    diagnosis: 'Pancreatitis',
    soap: {
      subjective: 'Vomiting decreased, tolerating small meals',
      objective: 'HR 100, RR 24, Abd discomfort mild',
      assessment: 'Acute pancreatitis resolving',
      plan: 'Fluids, antiemetic, analgesia, bland diet, recheck cPL',
    },
    meds: [{ name: 'Maropitant', dosageRangeMgKg: [1, 1], frequency: 'SID' }],
  },
];

const currencyToNumber = (raw: string | number | undefined) => {
  if (typeof raw === 'number') return raw;
  if (!raw) return 0;
  const cleaned = raw.replace(/[^\d.-]/g, '');
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : 0;
};

const normalizeStatus = (value: string | undefined) =>
  (value ?? 'Scheduled').replace(/\b\w/g, (char) => char.toUpperCase());

const deriveOpsSummary = (
  patients: Patient[],
  appointments: Appointment[],
  invoices: BillingWorkspacePayload['invoices'],
): OpsSummary => {
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayAppointments = appointments.filter((appt) => {
    const date = new Date(appt.date);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return date.toISOString().slice(0, 10) === todayKey;
  });

  const statusMap = new Map<string, number>();
  appointments.forEach((appt) => {
    const normalized = normalizeStatus(appt.status).toLowerCase();
    statusMap.set(normalized, (statusMap.get(normalized) ?? 0) + 1);
  });
  if (statusMap.size === 0) {
    ['check-in', 'pre-op', 'follow-up', 'completed'].forEach((status) =>
      statusMap.set(status, 0),
    );
  }

  let pendingRevenue = 0;
  let collectedRevenue = 0;
  invoices.forEach((invoice) => {
    const amount = currencyToNumber(
      typeof invoice === 'object' ? (invoice as any).amount ?? invoice.balance : 0,
    );
    const status = (invoice.status ?? 'pending').toLowerCase();
    if (status === 'paid' || status === 'collected') {
      collectedRevenue += amount;
    } else {
      pendingRevenue += amount;
    }
  });

  const appointmentStatus = Array.from(statusMap.entries()).map(([status, count]) => ({
    status: normalizeStatus(status),
    count,
  }));

  return {
    metrics: [
      {
        id: 'patients',
        label: 'Active Patients',
        value: patients.length,
        trend: '+12 this week',
      },
      {
        id: 'appointments',
        label: "Today?�?Appointments",
        value: todayAppointments.length || appointments.length,
        trend: `${statusMap.get('pre-op') ?? 0} awaiting prep`,
      },
      {
        id: 'surgery',
        label: 'Surgery Queue',
        value: statusMap.get('pre-op') ?? 0,
        trend: 'Operating rooms staffed',
      },
      {
        id: 'revenue',
        label: 'Pending Revenue',
        value: pendingRevenue,
        trend: `$${Math.round(collectedRevenue).toLocaleString()} collected`,
      },
    ],
    appointmentStatus,
    revenue: {
      pending: pendingRevenue,
      collected: collectedRevenue,
    },
  };
};

const fallbackOpsSummary = deriveOpsSummary(
  fallbackPatients,
  fallbackAppointments,
  fallbackBillingWorkspace.invoices,
);

// Query keys
export const patientsQueryKey = ['patients'] as const;
export const appointmentsQueryKey = ['appointments'] as const;
export const carePlansQueryKey = ['care-plans'] as const;
export const carePlanVersionsQueryKey = (carePlanId?: number | string, params?: Record<string, any>) => ['care-plan-versions', carePlanId ?? 'all', params ?? {}] as const;
export const carePlanVersionHistoryQueryKey = (versionId: string) => ['care-plan-version-history', versionId] as const;
export const carePlanVersionDiffQueryKey = (id: string, withId: string) => ['care-plan-version-diff', id, withId] as const;
export const billingWorkspaceQueryKey = ['billing-workspace'] as const;
export const opsSummaryQueryKey = ['ops-summary'] as const;
export const auditLogQueryKey = ['audit-log'] as const;
export const resourcesQueryKey = ['resources'] as const;
const resourceBookingsQueryKey = (resourceId?: string | number, start?: string | null, end?: string | null) =>
  ['resource-bookings', resourceId ?? 'all', start ?? 'any', end ?? 'any'] as const;
export const bedsWhiteboardQueryKey = ['beds-whiteboard'] as const;
export const icuWhiteboardQueryKey = ['icu-whiteboard'] as const;
export const handoffQueryKey = ['handoffs'] as const;
export const careOrderWorkflowQueryKey = (orderId: string) => ['care-order-workflow', orderId] as const;
export const soapTemplatesQueryKey = ['soap-templates'] as const;
export const inventoryItemsQueryKey = ['inventory-items'] as const;
export const inventoryAlertsQueryKey = ['inventory-alerts'] as const;
export const careOrdersQueryKey = (patientId?: string | number, filters?: {
  category?: string;
  status?: string;
  role?: string;
  priority?: string;
}) => ['care-orders', patientId ?? 'all', filters?.category ?? 'all', filters?.status ?? 'all', filters?.role ?? 'all', filters?.priority ?? 'all'] as const;
export const careOrderHistoryQueryKey = (orderId: string) => ['care-order-history', orderId] as const;
export const notificationsBaseQueryKey = ['notifications'] as const;
const notificationsQueryKey = (filters?: NotificationFilters) =>
  [
    ...notificationsBaseQueryKey,
    filters?.query ?? '',
    filters?.status ?? '',
    filters?.startDate ?? '',
    filters?.endDate ?? '',
    filters?.channel ?? '',
    filters?.page ?? 1,
    filters?.pageSize ?? 50,
  ] as const;
const clientErrorsQueryKey = (filters?: ClientErrorFilters) =>
  ['client-errors', filters?.query ?? '', filters?.startDate ?? '', filters?.endDate ?? '', filters?.page ?? 1, filters?.pageSize ?? 25] as const;
const patientNotesQueryKey = (patientId: string | number) => ['patient-notes', patientId] as const;
const patientPrescriptionsQueryKey = (patientId: string | number) => ['patient-prescriptions', patientId] as const;
const patientLabResultsQueryKey = (patientId: string | number) => ['patient-labs', patientId] as const;
const patientAttachmentsQueryKey = (patientId: string | number) => ['patient-attachments', patientId] as const;
const patientVisitsQueryKey = (patientId: string | number) => ['patient-visits', patientId] as const;
const patientRecordsQueryKey = (patientId: string | number) => ['patient-records', patientId] as const;

// Fetchers
export const login = async (credentials: { email: string; password: string }) => {
  const payload = await request('/api/auth/login', loginResponseSchema, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  setAuthToken(payload.token);
  return payload;
};

export const fetchPatients = async () => {
  try {
    const data = await request('/api/patients', patientsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to demo patients', error);
    return wrapValue(
      fallbackPatients,
      true,
      'Real-time patient registry unavailable. Showing demo roster.',
    );
  }
};

export const fetchAppointments = async () => {
  try {
    const data = await request('/api/appointments', appointmentsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to demo appointments', error);
    return wrapValue(
      fallbackAppointments,
      true,
      'Live appointment feed unreachable. Showing cached schedule.',
    );
  }
};

const fetchPatientVisits = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/visits`, visitsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached visits', error);
    const fallbackValue = getFallbackCollection(fallbackVisits, patientId);
    return wrapValue(fallbackValue, true, 'Visit log unreachable. Showing cached rounds.');
  }
};

const fetchMedicalRecords = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/records`, medicalRecordsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached medical records', error);
    const fallbackValue = getFallbackCollection(fallbackMedicalRecords, patientId);
    return wrapValue(fallbackValue, true, 'EMR service unreachable. Showing cached record.');
  }
};

export const fetchCarePlans = async () => {
  try {
    const data = await request('/api/care-plans', carePlanSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to static care plans', error);
    return wrapValue(
      fallbackCarePlans,
      true,
      'Care coordination service unreachable. Showing standby plan.',
    );
  }
};

// Care Plan Versioning Fetchers
export interface CarePlanVersionsQueryParams {
  page?: number;
  pageSize?: number;
  status?: string[];
  type?: string[];
  createdBy?: string;
  dateFrom?: string;
  dateTo?: string;
}
export interface PaginatedCarePlanVersions {
  items: CarePlanVersion[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
export const fetchCarePlanVersions = async (carePlanId?: number | string, params?: CarePlanVersionsQueryParams) => {
  const query = new URLSearchParams();
  if (carePlanId) query.set('carePlanId', String(carePlanId));
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  params?.status?.forEach(s => query.append('status', s));
  params?.type?.forEach(t => query.append('type', t));
  if (params?.createdBy) query.set('createdBy', params.createdBy);
  if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
  if (params?.dateTo) query.set('dateTo', params.dateTo);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  try {
    return await request(`/api/care-plans/versions${suffix}`, paginatedCarePlanVersionsSchema);
  } catch (err) {
    console.warn('care plan versions fetch failed', err);
    return { items: [], page: params?.page || 1, pageSize: params?.pageSize || 20, total: 0, totalPages: 0 } as PaginatedCarePlanVersions;
  }
};

export const createCarePlanVersion = async (payload: { carePlanId: number; content: any; summary?: string; changeNote?: string }) =>
  request('/api/care-plans/versions', carePlanVersionSchema, { method: 'POST', body: JSON.stringify(payload) });

export const fetchCarePlanVersion = async (id: string) =>
  request(`/api/care-plans/versions/${id}`, carePlanVersionSchema);

export const submitCarePlanVersion = async (id: string) =>
  request(`/api/care-plans/versions/${id}/submit`, carePlanVersionSchema, { method: 'POST' });

export const approveCarePlanVersion = async (id: string) =>
  request(`/api/care-plans/versions/${id}/approve`, carePlanVersionSchema, { method: 'POST' });

export const rejectCarePlanVersion = async (id: string, comment?: string) =>
  request(`/api/care-plans/versions/${id}/reject`, carePlanVersionSchema, { method: 'POST', body: JSON.stringify({ comment }) });

export const restoreCarePlanVersion = async (id: string) =>
  request(`/api/care-plans/versions/${id}/restore`, carePlanVersionSchema, { method: 'POST' });

export const diffCarePlanVersions = async (id: string, withId: string) =>
  request(`/api/care-plans/versions/${id}/diff?with=${withId}`, carePlanDiffSchema);

export const fetchCarePlanVersionHistory = async (id: string) =>
  request(`/api/care-plans/versions/${id}/history`, carePlanHistorySchema);

export const createCarePlanSignoffs = async (id: string, roles: string[]) =>
  request(`/api/care-plans/versions/${id}/signoffs`, z.array(carePlanSignoffSchema), {
    method: 'POST',
    body: JSON.stringify({ roles }),
  });

export const signCarePlanVersion = async (id: string, role: string) =>
  request(`/api/care-plans/versions/${id}/signoffs/${encodeURIComponent(role)}/sign`, carePlanSignoffSchema, { method: 'POST' });

export const revokeCarePlanVersionSignoff = async (id: string, role: string) =>
  request(`/api/care-plans/versions/${id}/signoffs/${encodeURIComponent(role)}/revoke`, carePlanSignoffSchema, { method: 'POST' });

export const fetchBillingWorkspace = async () => {
  try {
    const data = await request('/api/billing', billingSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached billing data', error);
    return wrapValue(
      fallbackBillingWorkspace,
      true,
      'Billing service offline. Displaying cached workspace.',
    );
  }
};

export const fetchOpsSummary = async () => {
  try {
    const data = await request('/api/ops/summary', opsSummarySchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached operations summary', error);
    return wrapValue(
      fallbackOpsSummary,
      true,
      'Operations summary offline. Displaying cached pulse.',
    );
  }
};

export interface AuditLogFilters {
  query?: string;
  startDate?: string;
  endDate?: string;
  action?: string;
}

export const fetchAuditLog = async (filters?: AuditLogFilters) => {
  const params = new URLSearchParams();
  if (filters?.query) params.set('q', filters.query);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.action && filters.action !== 'all') params.set('filter[action]', filters.action);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  try {
    const data = await request(`/api/audit-log${suffix}`, auditLogSchema);
    return wrapValue(data);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      throw error;
    }
    console.warn('Falling back to cached audit log', error);
    return wrapValue(
      fallbackAuditLog,
      true,
      'Audit service unavailable. Displaying recent cached activity.',
    );
  }
};

export const fetchResources = async () => {
  try {
    return await request('/api/schedule/resources', resourcesSchema);
  } catch (error) {
    console.warn('Falling back to cached resources', error);
    return wrapValue(fallbackResources, true, 'Resource service offline. Showing cached assets.');
  }
};

export const fetchResourceBookings = async (filters?: { resourceId?: string; start?: string; end?: string }) => {
  const query = new URLSearchParams();
  if (filters?.resourceId) query.set('resourceId', filters.resourceId);
  if (filters?.start) query.set('start', filters.start);
  if (filters?.end) query.set('end', filters.end);
  const path = `/api/schedule/bookings${query.toString() ? `?${query.toString()}` : ''}`;
  try {
    return await request(path, resourceBookingsSchema);
  } catch (error) {
    console.warn('Falling back to cached bookings', error);
    const filtered = fallbackResourceBookings.filter((b) => {
      if (filters?.resourceId && b.resourceId !== filters.resourceId) return false;
      return true;
    });
    return wrapValue(filtered, true, 'Booking service offline. Showing cached schedule.');
  }
};

export const fetchBedsWhiteboard = async () => {
  try {
    return await request('/api/beds/whiteboard', bedWhiteboardSchema);
  } catch (error) {
    console.warn('Falling back to cached bed whiteboard', error);
    const value = fallbackBeds.map((bed) => ({
      ...bed,
      stay: fallbackBedStays.find((stay) => stay.bedId === bed.id && stay.status === 'occupied') ?? null,
      tasks: fallbackCareTasks.filter((task) => {
        const stay = fallbackBedStays.find((s) => s.bedId === bed.id && s.status === 'occupied');
        return stay ? task.patientId === stay.patientId : false;
      }),
      handoffs: fallbackHandoffs.filter((handoff) => {
        const stay = fallbackBedStays.find((s) => s.bedId === bed.id && s.status === 'occupied');
        return stay ? handoff.patientId === stay.patientId : false;
      }),
    }));
    return wrapValue(value, true, 'Bed system offline. Showing cached layout.');
  }
};

export const fetchIcuWhiteboard = async () => {
  try {
    return await request('/api/whiteboard/icu', icuWhiteboardSchema);
  } catch (error) {
    console.warn('Falling back to cached ICU whiteboard', error);
    return wrapValue(
      fallbackBedStays.map((stay) => ({
        patientId: stay.patientId,
        bedId: stay.bedId,
        acuity: stay.acuity,
        attending: stay.attending,
        handoffNote: stay.handoffNote,
        tasks: fallbackCareTasks.filter((t) => t.patientId === stay.patientId),
        handoffs: fallbackHandoffs.filter((h) => h.patientId === stay.patientId),
      })),
      true,
      'ICU board offline. Showing cached tasks.',
    );
  }
};

export const fetchHandoffs = async () => {
  try {
    return await request('/api/handoffs', handoffListSchema);
  } catch (error) {
    console.warn('Falling back to cached handoffs', error);
    return wrapValue(fallbackHandoffs, true, 'Handoff service offline. Showing cached notes.');
  }
};

export const fetchSoapTemplates = async () => {
  try {
    return await request('/api/templates/soap', soapTemplateListSchema);
  } catch (error) {
    console.warn('Falling back to cached templates', error);
    return wrapValue(fallbackSoapTemplates, true, 'Template service offline. Showing cached templates.');
  }
};

export const generateSoapFromTemplate = (payload: { templateId?: string; diagnosis?: string; notes?: string }) =>
  request('/api/templates/soap', soapGeneratedSchema, { method: 'POST', body: JSON.stringify(payload) });

export const verifyMedicationSafety = (payload: {
  patientWeightKg?: number;
  medication: string;
  doseMgKg?: number;
  currentMeds?: string[];
}) => request('/api/safety/medication-check', medSafetySchema, { method: 'POST', body: JSON.stringify(payload) });

export const createResourceBooking = (payload: {
  resourceIds: string[];
  patientId?: string | number;
  appointmentId?: string | number;
  startTime: string;
  endTime: string;
  surgeon?: string;
  note?: string;
  allowWaitlist?: boolean;
}) =>
  request('/api/schedule/bookings', z.object({ created: z.array(resourceBookingSchema), conflicts: z.array(resourceBookingSchema) }), {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const assignBed = (payload: {
  bedId: string;
  patientId: string | number;
  plannedDischargeAt?: string;
  acuity?: string;
  attending?: string;
  handoffNote?: string;
}) => request('/api/beds/assign', bedStaySchema, { method: 'POST', body: JSON.stringify(payload) });

export const createHandoff = (payload: { patientId: string | number; summary: string; shift?: string; acuity?: string }) =>
  request('/api/handoffs', handoffSchema, { method: 'POST', body: JSON.stringify(payload) });

export const updateHandoff = (handoffId: string, payload: { summary?: string; shift?: string; acuity?: string; status?: string }) =>
  request(`/api/handoffs/${handoffId}`, handoffSchema, { method: 'PATCH', body: JSON.stringify(payload) });

export const deleteHandoff = (handoffId: string) =>
  request(`/api/handoffs/${handoffId}`, z.object({ deleted: z.boolean() }), { method: 'DELETE' });

export const updateResourceBooking = (bookingId: string, payload: {
  startTime?: string;
  endTime?: string;
  surgeon?: string;
  note?: string;
  status?: string;
}) =>
  request(`/api/schedule/bookings/${bookingId}`, resourceBookingSchema, { method: 'PATCH', body: JSON.stringify(payload) });

export const deleteResourceBooking = (bookingId: string) =>
  request(`/api/schedule/bookings/${bookingId}`, z.object({ deleted: z.boolean() }), { method: 'DELETE' });

export const promoteFromWaitlist = (bookingId: string) =>
  request(`/api/schedule/bookings/${bookingId}/promote`, resourceBookingSchema, { method: 'POST' });

export const fetchWaitlist = async (resourceId?: string) => {
  try {
    const path = resourceId ? `/api/schedule/waitlist?resourceId=${resourceId}` : '/api/schedule/waitlist';
    return await request(path, resourceBookingsSchema);
  } catch (error) {
    console.warn('Falling back to cached waitlist', error);
    const filtered = fallbackResourceBookings.filter((b) => b.waitlist || b.status === 'waitlist');
    return wrapValue(filtered, true, 'Waitlist service offline.');
  }
};

export const checkBedConflicts = async (bedId: string, startTime: string, endTime: string) => {
  try {
    return await request(
      `/api/beds/check-conflicts?bedId=${bedId}&start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`,
      z.object({ conflicts: z.array(bedStaySchema), available: z.boolean() })
    );
  } catch (error) {
    console.warn('Bed conflict check failed', error);
    return { conflicts: [], available: true };
  }
};

export const dischargePatient = (stayId: string, dischargeNote?: string) =>
  request(`/api/beds/discharge/${stayId}`, z.object({ discharged: z.boolean() }), {
    method: 'POST',
    body: JSON.stringify({ dischargeNote }),
  });

// Care Orders API
export const fetchCareOrders = async (
  patientId?: string | number,
  filters?: { category?: string; status?: string; role?: string; priority?: string }
) => {
  try {
    let path = patientId ? `/api/patients/${patientId}/orders` : '/api/orders';
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.priority) params.append('priority', filters.priority);
    if (params.toString()) path += `?${params.toString()}`;
    
    return await request(path, careOrderListSchema);
  } catch (error) {
    console.warn('Falling back to cached care orders', error);
    let filtered = fallbackCareOrders;
    if (patientId) filtered = filtered.filter((o) => o.patientId === patientId);
    if (filters?.category) filtered = filtered.filter((o) => o.category === filters.category);
    if (filters?.status) filtered = filtered.filter((o) => o.status === filters.status);
    if (filters?.role) filtered = filtered.filter((o) => o.assignedRole === filters.role);
    if (filters?.priority) filtered = filtered.filter((o) => o.priority === filters.priority);
    return wrapValue(filtered, true, 'Care orders service offline.');
  }
};

export const createCareOrder = (payload: {
  patientId: string | number;
  category: 'medication' | 'lab' | 'nursing' | 'followup';
  title: string;
  details: string;
  priority: 'Critical' | 'High' | 'Routine';
  assignedRole: string;
  assignedTo?: string;
  dueAt?: string;
}) =>
  request(`/api/patients/${payload.patientId}/orders`, careOrderSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateCareOrder = (orderId: string, payload: {
  title?: string;
  details?: string;
  priority?: string;
  status?: string;
  assignedRole?: string;
  assignedTo?: string;
  dueAt?: string;
  blockReason?: string;
}) =>
  request(`/api/orders/${orderId}`, careOrderSchema, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const acknowledgeCareOrder = (orderId: string) =>
  request(`/api/orders/${orderId}/ack`, careOrderSchema, { method: 'POST' });

export const completeCareOrder = (orderId: string, notes?: string) =>
  request(`/api/orders/${orderId}/complete`, careOrderSchema, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  });

export const blockCareOrder = (orderId: string, reason: string) =>
  request(`/api/orders/${orderId}/block`, careOrderSchema, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });

export const deleteCareOrder = (orderId: string) =>
  request(`/api/orders/${orderId}`, z.object({ deleted: z.boolean() }), { method: 'DELETE' });

export const fetchCareOrderHistory = async (orderId: string) => {
  try {
    return await request(`/api/orders/${orderId}/history`, careOrderHistoryListSchema);
  } catch (error) {
    console.warn('Falling back to empty order history', error);
    return wrapValue([], true, 'Order history unavailable.');
  }
};

export const fetchCareOrderWorkflow = (orderId: string) =>
  request(`/api/orders/${orderId}/signoffs`, careOrderWorkflowSchema);

export const approveCareOrderStep = (orderId: string, step: number, payload?: { note?: string; assignee?: string }) =>
  request(
    `/api/orders/${orderId}/steps/${step}/approve`,
    careOrderWorkflowSchema,
    { method: 'POST', body: JSON.stringify(payload ?? {}) },
  );

export const rejectCareOrderStep = (orderId: string, step: number, payload: { note: string; assignee?: string }) =>
  request(
    `/api/orders/${orderId}/steps/${step}/reject`,
    careOrderWorkflowSchema,
    { method: 'POST', body: JSON.stringify(payload) },
  );

export const createCareOrderHandoff = (
  orderId: string,
  payload: { note: string; toRole?: string; fromRole?: string },
) =>
  request(`/api/orders/${orderId}/handoff`, careOrderWorkflowSchema, {
    method: 'POST',
    body: JSON.stringify({ handoffNote: payload.note, toRole: payload.toRole, fromRole: payload.fromRole }),
  });

export const acceptCareOrderHandoff = (orderId: string, handoffId?: string) =>
  request(`/api/orders/${orderId}/handoff/accept`, careOrderWorkflowSchema, {
    method: 'POST',
    body: JSON.stringify({ handoffId }),
  });

const careOrderWorkflowSchema = z.object({
  order: careOrderSchema,
  signoffs: z.array(careOrderSignoffSchema),
  handoffs: z.array(careOrderHandoffSchema),
});
export type CareOrderWorkflow = z.infer<typeof careOrderWorkflowSchema>;

export const restoreCareOrderVersion = (orderId: string, version: number) =>
  request(`/api/orders/${orderId}/restore`, careOrderSchema, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });

export const createCareTask = (payload: {
  patientId: string | number;
  title: string;
  dueAt?: string;
  type?: string;
  status?: string;
  instructions?: string;
}) => request('/api/care-tasks', careTaskSchema, { method: 'POST', body: JSON.stringify(payload) });

export const createQuote = (payload: {
  client: string;
  patient: string;
  estimate?: string;
  validUntil?: string;
  signedBy?: string;
  convertToInvoice?: boolean;
}) => request('/api/finance/quote', quoteSchema, { method: 'POST', body: JSON.stringify(payload) });

export const enqueueNotification = (payload: { channel: string; to: string; message: string; scheduledAt?: string }) =>
  request('/api/notify', notifySchema, { method: 'POST', body: JSON.stringify(payload) });

export interface NotificationFilters {
  query?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  channel?: string;
  page?: number;
  pageSize?: number;
}

export const fetchNotifications = async (filters?: NotificationFilters) => {
  const params = new URLSearchParams();
  if (filters?.query) params.set('q', filters.query);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.channel) params.set('channel', filters.channel);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const defaultPayload: NotificationsResponse = {
    value: [],
    meta: {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 50,
      total: 0,
      totalPages: 1,
      hasMore: false,
    },
    fallback: true,
  };
  try {
    return await request(`/api/notifications${suffix}`, notificationsListSchema);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      throw error;
    }
    console.warn('Notifications fetch failed', error);
    return defaultPayload;
  }
};

export interface ClientErrorFilters {
  query?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export const fetchClientErrors = async (filters?: ClientErrorFilters) => {
  const params = new URLSearchParams();
  if (filters?.query) params.set('q', filters.query);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const defaults: z.infer<typeof clientErrorLogsSchema> = {
    value: [],
    meta: {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 25,
      total: 0,
      totalPages: 1,
      hasMore: false,
    },
    stats: { severityCounts: {} },
    fallback: true,
  };
  try {
    return await request(`/api/client-errors${suffix}`, clientErrorLogsSchema);
  } catch (error) {
    console.warn('Client errors fetch failed', error);
    return defaults;
  }
};

export const fetchInventoryItems = async () => {
  try {
    return await request('/api/inventory/items', inventoryItemsSchema);
  } catch (error) {
    console.warn('Falling back to cached inventory', error);
    return wrapValue([] as InventoryItem[], true, 'Inventory service offline');
  }
};

export const fetchInventoryAlerts = async () => {
  try {
    return await request('/api/inventory/alerts', inventoryAlertsSchema);
  } catch (error) {
    console.warn('Falling back to cached inventory alerts', error);
    return wrapValue([] as InventoryAlert[], true, 'Inventory alerts unavailable');
  }
};

export const consumeInventory = (payload: {
  itemId: string;
  quantity: number;
  reason?: string;
  refId?: string;
}) =>
  request(
    '/api/inventory/consume',
    z.object({
      movements: z.array(z.object({ batchId: z.string(), delta: z.number() })),
      alertCreated: z.string().nullable().optional(),
    }),
    { method: 'POST', body: JSON.stringify(payload) },
  );

const fetchPatientNotes = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/notes`, patientNotesSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached patient notes', error);
    return wrapValue(
      getFallbackPatientNotes(patientId),
      true,
      'Notes service offline. Displaying cached timeline.',
    );
  }
};

const fetchPrescriptions = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/prescriptions`, prescriptionsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached prescriptions', error);
    return wrapValue(
      getFallbackCollection(fallbackPrescriptions, patientId),
      true,
      'Prescription service offline. Displaying cached orders.',
    );
  }
};

const fetchLabResults = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/labs`, labResultsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached lab results', error);
    return wrapValue(
      getFallbackCollection(fallbackLabResults, patientId),
      true,
      'Lab reporting offline. Displaying cached findings.',
    );
  }
};

const fetchAttachments = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/attachments`, attachmentsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached attachments', error);
    return wrapValue(
      getFallbackCollection(fallbackAttachments, patientId),
      true,
      'Attachment vault unreachable. Displaying cached files.',
    );
  }
};

const persistPatientNote = async (
  patientId: string | number,
  payload: { note: string; category?: string; author?: string; role?: string },
) =>
  request(`/api/patients/${patientId}/notes`, patientNoteSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistPrescription = async (
  patientId: string | number,
  payload: {
    medication: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
    status?: string;
    inventoryItemId?: string;
    inventoryQuantity?: number;
  },
) =>
  request(`/api/patients/${patientId}/prescriptions`, prescriptionSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistLabResult = async (
  patientId: string | number,
  payload: {
    testName: string;
    status?: string;
    findings?: string;
    performedAt?: string | null;
    attachmentId?: string | null;
  },
) =>
  request(`/api/patients/${patientId}/labs`, labResultSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistAttachment = async (
  patientId: string | number,
  payload: {
    filename: string;
    contentType?: string;
    contentBase64: string;
    source?: string;
    recordId?: string;
  },
) =>
  request(`/api/patients/${patientId}/attachments`, attachmentSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistVisit = async (
  patientId: string | number,
  payload: { vetId?: string | number; visitDate?: string; reason?: string; disposition?: string },
) =>
  request(`/api/patients/${patientId}/visits`, visitSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistVisitUpdate = async (
  patientId: string | number,
  visitId: string,
  payload: { vetId?: string | number; visitDate?: string; reason?: string; disposition?: string },
) =>
  request(`/api/patients/${patientId}/visits/${visitId}`, visitSchema, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

const persistMedicalRecord = async (
  patientId: string | number,
  payload: {
    visitId?: string;
    diagnosis?: string;
    assessment?: string;
    treatmentPlan?: string;
    vitals?: string;
    labSummary?: string;
    imagingSummary?: string;
    notes?: string;
  },
) =>
  request(`/api/patients/${patientId}/records`, medicalRecordSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistMedicalRecordUpdate = async (
  patientId: string | number,
  recordId: string,
  payload: {
    visitId?: string;
    diagnosis?: string;
    assessment?: string;
    treatmentPlan?: string;
    vitals?: string;
    labSummary?: string;
    imagingSummary?: string;
    notes?: string;
  },
) =>
  request(`/api/patients/${patientId}/records/${recordId}`, medicalRecordSchema, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

const persistCarePlans = async (payload: CarePlanPayload) =>
  request('/api/care-plans', carePlanSchema, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

const advanceAppointmentStatus = async (
  appointmentId: string | number,
  payload: { status?: string; note?: string },
) =>
  request(`/api/appointments/${appointmentId}/advance`, appointmentActionSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const sendAppointmentReminder = async (
  appointmentId: string | number,
  payload: { channel?: string; message?: string },
) =>
  request(`/api/appointments/${appointmentId}/reminder`, reminderResponseSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const markInvoicePaid = async (invoiceId: string, payload?: { reference?: string }) =>
  request(`/api/billing/${invoiceId}/mark-paid`, billingActionSchema, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });

const markNotificationRead = async (notificationId: string) =>
  request(`/api/notifications/${notificationId}/read`, notifySchema, {
    method: 'POST',
  });

const sendInvoiceReminder = async (invoiceId: string, payload?: { channel?: string; message?: string }) =>
  request(`/api/billing/${invoiceId}/remind`, reminderResponseSchema, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });

// Hooks
export const usePatients = () => {
  const query = useQuery({
    queryKey: patientsQueryKey,
    queryFn: fetchPatients,
    placeholderData: () => wrapValue(fallbackPatients, true),
  });
  return toHookResult(query, fallbackPatients);
};

export const useAppointments = () => {
  const query = useQuery({
    queryKey: appointmentsQueryKey,
    queryFn: fetchAppointments,
    placeholderData: () => wrapValue(fallbackAppointments, true),
  });
  return toHookResult(query, fallbackAppointments);
};

export const useCarePlans = () => {
  const query = useQuery({
    queryKey: carePlansQueryKey,
    queryFn: fetchCarePlans,
    placeholderData: () => wrapValue(fallbackCarePlans, true),
  });
  return toHookResult(query, fallbackCarePlans);
};

// Care Plan Versioning Hooks
export const useCarePlanVersions = (carePlanId?: number | string, params?: CarePlanVersionsQueryParams) => {
  const query = useQuery({
    queryKey: carePlanVersionsQueryKey(carePlanId, params),
    queryFn: () => fetchCarePlanVersions(carePlanId, params),
  });
  return query as UseQueryResult<PaginatedCarePlanVersions>;
};

export const useCarePlanVersion = (versionId?: string) => {
  const enabled = Boolean(versionId);
  const query = useQuery({
    queryKey: ['care-plan-version', versionId ?? 'noop'],
    queryFn: () => fetchCarePlanVersion(versionId!),
    enabled,
  });
  return query as UseQueryResult<CarePlanVersion>;
};

export const useCarePlanVersionHistory = (versionId?: string) => {
  const enabled = Boolean(versionId);
  const query = useQuery({
    queryKey: versionId ? carePlanVersionHistoryQueryKey(versionId) : ['care-plan-version-history','noop'],
    queryFn: () => fetchCarePlanVersionHistory(versionId!),
    enabled,
  });
  return query as UseQueryResult<CarePlanHistory>;
};

export const useCarePlanVersionDiff = (baseId?: string, otherId?: string) => {
  const enabled = Boolean(baseId && otherId);
  const query = useQuery({
    queryKey: baseId && otherId ? carePlanVersionDiffQueryKey(baseId, otherId) : ['care-plan-version-diff','noop'],
    queryFn: () => diffCarePlanVersions(baseId!, otherId!),
    enabled,
  });
  return query as UseQueryResult<CarePlanDiff>;
};

export const useCreateCarePlanVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCarePlanVersion,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: carePlanVersionsQueryKey(created.carePlanId) });
    },
  });
};

const simpleInvalidate = (qc: ReturnType<typeof useQueryClient>, carePlanId: number) => {
  qc.invalidateQueries({ queryKey: carePlanVersionsQueryKey(carePlanId) });
};

export const useSubmitCarePlanVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitCarePlanVersion,
    onSuccess: (v) => simpleInvalidate(qc, v.carePlanId),
  });
};

export const useApproveCarePlanVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: approveCarePlanVersion,
    onSuccess: (v) => simpleInvalidate(qc, v.carePlanId),
  });
};

export const useRejectCarePlanVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) => rejectCarePlanVersion(id, comment),
    onSuccess: (v) => simpleInvalidate(qc, v.carePlanId),
  });
};

export const useRestoreCarePlanVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: restoreCarePlanVersion,
    onSuccess: (v) => simpleInvalidate(qc, v.carePlanId),
  });
};

export const useCreateCarePlanSignoffs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => createCarePlanSignoffs(id, roles),
    onSuccess: (_signoffs, vars) => {
      qc.invalidateQueries({ queryKey: carePlanVersionHistoryQueryKey(vars.id) });
    },
  });
};

export const useSignCarePlanVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => signCarePlanVersion(id, role),
    onSuccess: (_signoff, vars) => {
      qc.invalidateQueries({ queryKey: carePlanVersionHistoryQueryKey(vars.id) });
    },
  });
};

export const useRevokeCarePlanVersionSignoff = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => revokeCarePlanVersionSignoff(id, role),
    onSuccess: (_signoff, vars) => {
      qc.invalidateQueries({ queryKey: carePlanVersionHistoryQueryKey(vars.id) });
    },
  });
};

export const useSaveCarePlans = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CarePlanPayload) => persistCarePlans(payload),
    onSuccess: (payload) => {
      queryClient.setQueryData<QueryEnvelope<CarePlanPayload>>(carePlansQueryKey, {
        value: payload,
        fallback: false,
      });
    },
  });
};

export const useBillingWorkspace = () => {
  const query = useQuery({
    queryKey: billingWorkspaceQueryKey,
    queryFn: fetchBillingWorkspace,
    placeholderData: () => wrapValue(fallbackBillingWorkspace, true),
  });
  return toHookResult(query, fallbackBillingWorkspace);
};

export const useOpsSummary = () => {
  const query = useQuery({
    queryKey: opsSummaryQueryKey,
    queryFn: fetchOpsSummary,
    placeholderData: () => wrapValue(fallbackOpsSummary, true),
  });
  return toHookResult(query, fallbackOpsSummary);
};

export const useAuditLog = (enabled = true, filters?: AuditLogFilters) => {
  const filterKey = [
    filters?.query ?? '',
    filters?.startDate ?? '',
    filters?.endDate ?? '',
    filters?.action ?? '',
  ];
  const query = useQuery({
    queryKey: [...auditLogQueryKey, ...filterKey],
    queryFn: () => fetchAuditLog(filters),
    enabled,
    placeholderData: () => wrapValue(fallbackAuditLog, true),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
  return toHookResult(query, fallbackAuditLog);
};

export const useResources = () => {
  const query = useQuery({
    queryKey: resourcesQueryKey,
    queryFn: fetchResources,
    placeholderData: () => wrapValue(fallbackResources, true),
  });
  return toHookResult(query, fallbackResources);
};

export const useResourceBookings = (filters?: { resourceId?: string; start?: string; end?: string }) => {
  const query = useQuery({
    queryKey: resourceBookingsQueryKey(filters?.resourceId, filters?.start ?? null, filters?.end ?? null),
    queryFn: () => fetchResourceBookings(filters),
    placeholderData: () => wrapValue(fallbackResourceBookings, true),
  });
  return toHookResult(query, fallbackResourceBookings);
};

export const useBedsWhiteboard = () => {
  const query = useQuery({
    queryKey: bedsWhiteboardQueryKey,
    queryFn: fetchBedsWhiteboard,
    placeholderData: () => wrapValue([], true),
  });
  return toHookResult(query, [] as BedWhiteboard);
};

export const useIcuWhiteboard = () => {
  const query = useQuery({
    queryKey: icuWhiteboardQueryKey,
    queryFn: fetchIcuWhiteboard,
    placeholderData: () => wrapValue([], true),
  });
  return toHookResult(query, [] as IcuWhiteboard);
};

export const useHandoffs = () => {
  const query = useQuery({
    queryKey: handoffQueryKey,
    queryFn: fetchHandoffs,
    placeholderData: () => wrapValue(fallbackHandoffs, true),
  });
  return toHookResult(query, fallbackHandoffs);
};

export const useSoapTemplates = () => {
  const query = useQuery({
    queryKey: soapTemplatesQueryKey,
    queryFn: fetchSoapTemplates,
    placeholderData: () => wrapValue(fallbackSoapTemplates, true),
  });
  return toHookResult(query, fallbackSoapTemplates);
};

export const useInventoryItems = () => {
  const query = useQuery({
    queryKey: inventoryItemsQueryKey,
    queryFn: fetchInventoryItems,
    placeholderData: () => wrapValue([] as InventoryItem[], true),
  });
  return toHookResult(query, [] as InventoryItem[]);
};

export const useInventoryAlerts = () => {
  const query = useQuery({
    queryKey: inventoryAlertsQueryKey,
    queryFn: fetchInventoryAlerts,
    placeholderData: () => wrapValue([] as InventoryAlert[], true),
  });
  return toHookResult(query, [] as InventoryAlert[]);
};

export const usePatientNotes = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackNotes = patientId ? getFallbackPatientNotes(patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientNotesQueryKey(patientId) : ['patient-notes', 'noop'],
    queryFn: () => fetchPatientNotes(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackNotes, true),
  });

  if (!enabled) {
    return {
      ...query,
      data: [] as PatientNote[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }

  return toHookResult(query as UseQueryResult<QueryEnvelope<PatientNote[]>>, fallbackNotes);
};

export const usePatientPrescriptions = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackPrescriptions, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientPrescriptionsQueryKey(patientId) : ['patient-prescriptions', 'noop'],
    queryFn: () => fetchPrescriptions(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as Prescription[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<Prescription[]>>, fallbackValue);
};

export const usePatientLabResults = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackLabResults, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientLabResultsQueryKey(patientId) : ['patient-labs', 'noop'],
    queryFn: () => fetchLabResults(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as LabResult[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<LabResult[]>>, fallbackValue);
};

export const usePatientAttachments = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackAttachments, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientAttachmentsQueryKey(patientId) : ['patient-attachments', 'noop'],
    queryFn: () => fetchAttachments(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as PatientAttachment[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<PatientAttachment[]>>, fallbackValue);
};

export const usePatientVisits = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackVisits, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientVisitsQueryKey(patientId) : ['patient-visits', 'noop'],
    queryFn: () => fetchPatientVisits(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as PatientVisit[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<PatientVisit[]>>, fallbackValue);
};

export const usePatientRecords = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackMedicalRecords, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientRecordsQueryKey(patientId) : ['patient-records', 'noop'],
    queryFn: () => fetchMedicalRecords(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as MedicalRecord[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<MedicalRecord[]>>, fallbackValue);
};

// Mutations
const buildLocalPatient = (payload: PatientFormData): Patient => ({
  id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Date.now().toString(),
  name: payload.name.trim(),
  age: Number(payload.age),
  species: payload.species.trim().toLowerCase(),
  owner: payload.ownerName.trim(),
  doctor: 'Dr. Avery',
  lastVisit: new Date().toLocaleDateString(),
});

const persistPatient = async (payload: PatientFormData): Promise<Patient> => {
  try {
    return await request('/api/patients', patientSchema, {
      method: 'POST',
      body: JSON.stringify({
        name: payload.name,
        age: Number(payload.age),
        species: payload.species,
        owner: payload.ownerName,
        contact: payload.contact,
      }),
    });
  } catch (error) {
    console.warn('Unable to reach API, storing patient locally', error);
    return buildLocalPatient(payload);
  }
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: persistPatient,
    onSuccess: (newPatient) => {
      queryClient.setQueryData<QueryEnvelope<Patient[]>>(patientsQueryKey, (existing) => {
        const current = existing?.value ?? [];
        return {
          value: [newPatient, ...current],
          fallback: existing?.fallback ?? false,
          message: existing?.message,
        };
      });
    },
  });
};

export const useCreatePrescription = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistPrescription>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistPrescription(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<Prescription[]>>(
        patientPrescriptionsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useCreateLabResult = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistLabResult>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistLabResult(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<LabResult[]>>(
        patientLabResultsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useUploadAttachment = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistAttachment>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistAttachment(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientAttachment[]>>(
        patientAttachmentsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useAddPatientNote = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { note: string; category?: string; author?: string; role?: string }) => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return persistPatientNote(patientId, payload);
    },
    onSuccess: (note) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientNote[]>>(patientNotesQueryKey(patientId), (existing) => {
        const current = existing?.value ?? [];
        return {
          value: [note, ...current],
          fallback: existing?.fallback ?? false,
          message: existing?.message,
        };
      });
    },
  });
};

export const useCreateVisit = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistVisit>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistVisit(patientId, payload);
    },
    onSuccess: (visit) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientVisit[]>>(patientVisitsQueryKey(patientId), (existing) => {
        if (!existing) return existing;
        return {
          ...existing,
          value: [visit, ...(existing.value ?? [])],
        };
      });
    },
  });
};

export const useUpdateVisit = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { visitId: string; vetId?: string | number; visitDate?: string; reason?: string; disposition?: string }) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistVisitUpdate(patientId, payload.visitId, payload);
    },
    onSuccess: (visit) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientVisit[]>>(patientVisitsQueryKey(patientId), (existing) => {
        if (!existing) return existing;
        const next = (existing.value ?? []).map((v) => (v.id === visit.id ? visit : v));
        return { ...existing, value: next };
      });
    },
  });
};

export const useCreateMedicalRecord = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistMedicalRecord>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistMedicalRecord(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<MedicalRecord[]>>(patientRecordsQueryKey(patientId), (existing) => {
        if (!existing) return existing;
        return {
          ...existing,
          value: [record, ...(existing.value ?? [])],
        };
      });
    },
  });
};

export const useUpdateMedicalRecord = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { recordId: string } & Parameters<typeof persistMedicalRecordUpdate>[2]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistMedicalRecordUpdate(patientId, payload.recordId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<MedicalRecord[]>>(patientRecordsQueryKey(patientId), (existing) => {
        if (!existing) return existing;
        const next = (existing.value ?? []).map((r) => (r.id === record.id ? record : r));
        return { ...existing, value: next };
      });
    },
  });
};

export const useAdvanceAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { appointmentId: string | number; status?: string; note?: string }) =>
      advanceAppointmentStatus(payload.appointmentId, { status: payload.status, note: payload.note }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<QueryEnvelope<Appointment[]>>(appointmentsQueryKey, (existing) => {
        if (!existing) return existing;
        const next = existing.value.map((appt) =>
          String(appt.id) === String(variables.appointmentId)
            ? { ...appt, status: result.status ?? appt.status }
            : appt,
        );
        return { ...existing, value: next };
      });
    },
  });
};

export const useAppointmentReminder = () =>
  useMutation({
    mutationFn: (payload: { appointmentId: string | number; channel?: string; message?: string }) =>
      sendAppointmentReminder(payload.appointmentId, { channel: payload.channel, message: payload.message }),
  });

export const useMarkInvoicePaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { invoiceId: string; reference?: string }) =>
      markInvoicePaid(payload.invoiceId, payload.reference ? { reference: payload.reference } : undefined),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<QueryEnvelope<BillingWorkspacePayload>>(billingWorkspaceQueryKey, (existing) => {
        if (!existing) return existing;
        const next = {
          ...existing.value,
          invoices: existing.value.invoices.map((invoice) =>
            invoice.id === variables.invoiceId
              ? { ...invoice, status: result.status ?? 'Paid', balance: result.balance ?? '$0' }
              : invoice,
          ),
        };
        return { ...existing, value: next };
      });
    },
  });
};

export const useInvoiceReminder = () =>
  useMutation({
    mutationFn: (payload: { invoiceId: string; channel?: string; message?: string }) =>
      sendInvoiceReminder(payload.invoiceId, { channel: payload.channel, message: payload.message }),
  });

export const useCreateResourceBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createResourceBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceBookingsQueryKey(undefined, null, null) });
    },
  });
};

export const useAssignBed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignBed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
    },
  });
};

export const useCreateHandoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHandoff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: handoffQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
    },
  });
};

export const useUpdateHandoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ handoffId, payload }: { handoffId: string; payload: { summary?: string; shift?: string; acuity?: string; status?: string } }) =>
      updateHandoff(handoffId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: handoffQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
    },
  });
};

export const useDeleteHandoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHandoff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: handoffQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
    },
  });
};

export const useUpdateResourceBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, payload }: { bookingId: string; payload: { startTime?: string; endTime?: string; surgeon?: string; note?: string; status?: string } }) =>
      updateResourceBooking(bookingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceBookingsQueryKey(undefined, null, null) });
    },
  });
};

export const useDeleteResourceBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteResourceBooking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceBookingsQueryKey(undefined, null, null) });
    },
  });
};

export const usePromoteFromWaitlist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: promoteFromWaitlist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceBookingsQueryKey(undefined, null, null) });
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });
};

export const useWaitlist = (resourceId?: string) => {
  const query = useQuery({
    queryKey: ['waitlist', resourceId ?? 'all'],
    queryFn: () => fetchWaitlist(resourceId),
    placeholderData: () => wrapValue([], true),
  });
  return toHookResult(query, []);
};

export const useBedConflicts = (bedId: string, startTime: string, endTime: string) => {
  const enabled = Boolean(bedId && startTime && endTime);
  return useQuery({
    queryKey: ['bed-conflicts', bedId, startTime, endTime],
    queryFn: () => checkBedConflicts(bedId, startTime, endTime),
    enabled,
  });
};

export const useDischargePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stayId, dischargeNote }: { stayId: string; dischargeNote?: string }) =>
      dischargePatient(stayId, dischargeNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
    },
  });
};

export const useCreateCareTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCareTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bedsWhiteboardQueryKey });
      queryClient.invalidateQueries({ queryKey: icuWhiteboardQueryKey });
    },
  });
};

export const useMedicationSafety = () => useMutation({ mutationFn: verifyMedicationSafety });

export const useGenerateSoap = () => useMutation({ mutationFn: generateSoapFromTemplate });

export const useCreateQuote = () => useMutation({ mutationFn: createQuote });

export const useNotify = () => useMutation({ mutationFn: enqueueNotification });

export const useNotifications = (filters?: NotificationFilters) => {
  const placeholder: NotificationsResponse = {
    value: [],
    meta: {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 50,
      total: 0,
      totalPages: 1,
      hasMore: false,
    },
    fallback: true,
  };
  const query = useQuery({
    queryKey: notificationsQueryKey(filters),
    queryFn: () => fetchNotifications(filters),
    placeholderData: () => placeholder,
  });
  return { ...query, data: query.data ?? placeholder };
};

export const useClientErrors = (filters?: ClientErrorFilters) => {
  const placeholder: z.infer<typeof clientErrorLogsSchema> = {
    value: [],
    meta: {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 25,
      total: 0,
      totalPages: 1,
      hasMore: false,
    },
    stats: { severityCounts: {} },
    fallback: true,
  };
  const query = useQuery({
    queryKey: clientErrorsQueryKey(filters),
    queryFn: () => fetchClientErrors(filters),
    placeholderData: () => placeholder,
  });
  return { ...query, data: query.data ?? placeholder };
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { notificationId: string }) => markNotificationRead(payload.notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsBaseQueryKey });
    },
  });
};

export const downloadReport = async (params: { topic: string; format?: 'csv' | 'xlsx' }) => {
  const topic = params.topic;
  const format = params.format ?? 'csv';
  const accept =
    format === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';
  const res = await fetch(`/api/reports/export?topic=${encodeURIComponent(topic)}&format=${format}`, {
    headers: { Accept: accept },
  });
  if (!res.ok) {
    throw new ApiError(`Failed to download report (${res.status})`, res.status);
  }
  const blob = await res.blob();
  return blob;
};

export const fetchKpi = async () => request('/api/reports/kpi', z.any());

export const useConsumeInventory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: consumeInventory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryItemsQueryKey });
      queryClient.invalidateQueries({ queryKey: inventoryAlertsQueryKey });
    },
  });
};

// Care Orders Hooks
export const useCareOrders = (
  patientId?: string | number,
  filters?: { category?: string; status?: string; role?: string; priority?: string }
) => {
  const query = useQuery({
    queryKey: careOrdersQueryKey(patientId, filters),
    queryFn: () => fetchCareOrders(patientId, filters),
    placeholderData: () => wrapValue([], true),
  });
  return toHookResult(query, []);
};

export const useCareOrderHistory = (orderId: string) => {
  const enabled = Boolean(orderId);
  const query = useQuery({
    queryKey: careOrderHistoryQueryKey(orderId),
    queryFn: () => fetchCareOrderHistory(orderId),
    enabled,
    placeholderData: () => wrapValue([], true),
  });
  return toHookResult(query, []);
};

export const useCareOrderWorkflow = (orderId?: string, enabled = true) => {
  return useQuery({
    queryKey: careOrderWorkflowQueryKey(orderId ?? 'none'),
    queryFn: () => fetchCareOrderWorkflow(orderId as string),
    enabled: Boolean(orderId && enabled),
  });
};

const applyOrderUpdateToCache = (queryClient: ReturnType<typeof useQueryClient>, order: CareOrder) => {
  const snapshots = queryClient.getQueriesData<QueryEnvelope<CareOrder[]>>({ queryKey: careOrdersQueryKey() });
  snapshots.forEach(([key, value]) => {
    if (!value?.value) return;
    const updated = value.value.map((existing) => (existing.id === order.id ? order : existing));
    queryClient.setQueryData(key, wrapValue(updated, value.fallback, value.message));
  });
};

export const useApproveCareOrderStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, step, payload }: { orderId: string; step: number; payload?: { note?: string; assignee?: string } }) =>
      approveCareOrderStep(orderId, step, payload),
    onSuccess: (data, { orderId }) => {
      applyOrderUpdateToCache(queryClient, data.order);
      queryClient.setQueryData(careOrderWorkflowQueryKey(orderId), data);
    },
    onSettled: (_data, _error, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
      queryClient.invalidateQueries({ queryKey: careOrderWorkflowQueryKey(orderId) });
    },
  });
};

export const useRejectCareOrderStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, step, payload }: { orderId: string; step: number; payload: { note: string; assignee?: string } }) =>
      rejectCareOrderStep(orderId, step, payload),
    onSuccess: (data, { orderId }) => {
      applyOrderUpdateToCache(queryClient, data.order);
      queryClient.setQueryData(careOrderWorkflowQueryKey(orderId), data);
    },
    onSettled: (_data, _error, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
      queryClient.invalidateQueries({ queryKey: careOrderWorkflowQueryKey(orderId) });
    },
  });
};

export const useCreateCareOrderHandoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: { note: string; toRole?: string; fromRole?: string } }) =>
      createCareOrderHandoff(orderId, payload),
    onSuccess: (data, { orderId }) => {
      applyOrderUpdateToCache(queryClient, data.order);
      queryClient.setQueryData(careOrderWorkflowQueryKey(orderId), data);
    },
    onSettled: (_data, _error, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
      queryClient.invalidateQueries({ queryKey: careOrderWorkflowQueryKey(orderId) });
    },
  });
};

export const useAcceptCareOrderHandoff = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, handoffId }: { orderId: string; handoffId?: string }) =>
      acceptCareOrderHandoff(orderId, handoffId),
    onSuccess: (data, { orderId }) => {
      applyOrderUpdateToCache(queryClient, data.order);
      queryClient.setQueryData(careOrderWorkflowQueryKey(orderId), data);
    },
    onSettled: (_data, _error, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
      queryClient.invalidateQueries({ queryKey: careOrderWorkflowQueryKey(orderId) });
    },
  });
};

export const useCreateCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCareOrder,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey(variables.patientId) });
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useUpdateCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, payload }: { orderId: string; payload: any }) =>
      updateCareOrder(orderId, payload),
    onMutate: async ({ orderId, payload }: { orderId: string; payload: any }) => {
      await queryClient.cancelQueries({ queryKey: careOrdersQueryKey() });
      const previous = queryClient.getQueriesData<QueryEnvelope<CareOrder[]>>({ queryKey: careOrdersQueryKey() });
      previous.forEach(([key, value]) => {
        if (!value?.value) return;
        const updated = value.value.map(o => {
          if (o.id !== orderId) return o;
          const next: CareOrder = {
            ...o,
            ...payload,
            // normalize blockReason removal (null or undefined means cleared)
            blockReason: payload?.blockReason === null || payload?.blockReason === undefined ? undefined : payload.blockReason,
            version: o.version + 1,
            updatedAt: new Date().toISOString(),
          };
          return next;
        });
        queryClient.setQueryData(key as any, wrapValue(updated, value.fallback, value.message));
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, value]: any) => queryClient.setQueryData(key, value));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useAcknowledgeCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acknowledgeCareOrder,
    onMutate: async (orderId: string) => {
      await queryClient.cancelQueries({ queryKey: careOrdersQueryKey() });
      const previous = queryClient.getQueriesData<QueryEnvelope<CareOrder[]>>({ queryKey: careOrdersQueryKey() });
      previous.forEach(([key, value]) => {
        if (value?.value) {
          const updated = value.value.map(o => o.id === orderId ? { ...o, status: 'acknowledged', version: o.version + 1 } : o);
          queryClient.setQueryData(key as any, wrapValue(updated, value.fallback, value.message));
        }
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, value]: any) => queryClient.setQueryData(key, value));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useCompleteCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, notes }: { orderId: string; notes?: string }) =>
      completeCareOrder(orderId, notes),
    onMutate: async ({ orderId }: { orderId: string; notes?: string }) => {
      await queryClient.cancelQueries({ queryKey: careOrdersQueryKey() });
      const previous = queryClient.getQueriesData<QueryEnvelope<CareOrder[]>>({ queryKey: careOrdersQueryKey() });
      previous.forEach(([key, value]) => {
        if (value?.value) {
          const updated = value.value.map(o => o.id === orderId ? { ...o, status: 'done', version: o.version + 1 } : o);
          queryClient.setQueryData(key as any, wrapValue(updated, value.fallback, value.message));
        }
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, value]: any) => queryClient.setQueryData(key, value));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useBlockCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: string; reason: string }) =>
      blockCareOrder(orderId, reason),
    onMutate: async ({ orderId, reason }: { orderId: string; reason: string }) => {
      await queryClient.cancelQueries({ queryKey: careOrdersQueryKey() });
      const previous = queryClient.getQueriesData<QueryEnvelope<CareOrder[]>>({ queryKey: careOrdersQueryKey() });
      previous.forEach(([key, value]) => {
        if (value?.value) {
          const updated = value.value.map(o => o.id === orderId ? { ...o, status: 'blocked', blockReason: reason, version: o.version + 1 } : o);
          queryClient.setQueryData(key as any, wrapValue(updated, value.fallback, value.message));
        }
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, value]: any) => queryClient.setQueryData(key, value));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useDeleteCareOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCareOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const useRestoreCareOrderVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, version }: { orderId: string; version: number }) =>
      restoreCareOrderVersion(orderId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careOrdersQueryKey() });
    },
  });
};

export const __private__ = {
  fallbackPatients,
  fallbackAppointments,
  fallbackCarePlans,
  fallbackBillingWorkspace,
  fallbackOpsSummary,
  getFallbackPatientNotes,
  fallbackPrescriptions,
  fallbackLabResults,
  fallbackAttachments,
  fallbackVisits,
  fallbackMedicalRecords,
  fallbackAuditLog,
  fallbackResources,
  fallbackResourceBookings,
  fallbackBeds,
  fallbackBedStays,
  fallbackHandoffs,
  fallbackCareTasks,
  fallbackCareOrders,
  fallbackSoapTemplates,
  wrapValue,
};
